import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  Swords, 
  Users, 
  Calendar, 
  Trophy, 
  Target,
  Settings,
  MapPin,
  Zap,
  TrendingUp,
  Activity,
  Mountain,
  Clock
} from 'lucide-react';
import api from '../services/api';
import defaultGroupPic from '../assets/defaultGroupPic.jpg'
const CreateClubWar = ({ clubId, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    clubDefieId: '',
    typeDefi: 'distance_totale',
    dateDebut: '',
    dateFin: '',
    pointsVainqueur: 100,
    pointsPerdant: 0,
    regles: {
      sportType: 'run',
      distanceMin: 0,
      allureMin: null,
      allureMax: null
    }
  });

  useEffect(() => {
    fetchClubs();
    // Définir date de début à demain
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    // Définir date de fin à dans 7 jours
    const weekLater = new Date(tomorrow);
    weekLater.setDate(weekLater.getDate() + 7);
    
    setFormData(prev => ({
      ...prev,
      dateDebut: tomorrow.toISOString().split('T')[0],
      dateFin: weekLater.toISOString().split('T')[0]
    }));
  }, []);

  const fetchClubs = async () => {
    try {
      const response = await api.get('/clubs');
      const filteredClubs = Array.isArray(response.data) 
        ? response.data.filter(club => club._id !== clubId)
        : [];
      setClubs(filteredClubs);
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
      setClubs([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const warData = {
        ...formData,
        clubInitiateurId: clubId
      };

      const response = await api.post('/club-wars', warData);
      
      alert('Défi lancé avec succès ! Le club adverse doit maintenant l\'accepter.');
      if (onClose) onClose();
      navigate(`/club-war/${response.data.clubWar._id}`);
    } catch (error) {
      console.error('Erreur lors de la création de la guerre:', error);
      alert(error.response?.data?.message || 'Erreur lors de la création du défi');
    } finally {
      setLoading(false);
    }
  };

  const typeDefiOptions = [
    { 
      value: 'distance_totale', 
      label: 'Distance totale', 
      description: 'Kilomètres cumulés',
      icon: Target,
      color: 'indigo'
    },
    { 
      value: 'nombre_courses', 
      label: 'Nombre d\'activités', 
      description: 'Total des sorties',
      icon: Activity,
      color: 'blue'
    },
    { 
      value: 'distance_moyenne', 
      label: 'Distance moyenne', 
      description: 'Par participant',
      icon: TrendingUp,
      color: 'purple'
    },
    { 
      value: 'participation', 
      label: 'Taux de participation', 
      description: 'Pourcentage de membres',
      icon: Users,
      color: 'green'
    },
    { 
      value: 'denivele_total', 
      label: 'Dénivelé total', 
      description: 'Mètres cumulés',
      icon: Mountain,
      color: 'orange'
    },
    { 
      value: 'vitesse_moyenne', 
      label: 'Allure moyenne', 
      description: 'Vitesse du club',
      icon: Zap,
      color: 'yellow'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      indigo: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/20',
      blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20',
      purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20',
      green: 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20',
      orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400 hover:bg-orange-500/20',
      yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20',
    };
    return colors[color];
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border-b border-slate-700 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="w-12 h-12 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Swords size={24} className="text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Lancer une Guerre
                </h2>
                <p className="text-gray-400 text-sm">
                  Défiez un autre club dans un challenge sportif
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white hover:bg-slate-800 rounded-lg p-2 transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Nom du défi */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Nom du défi *
            </label>
            <input
              type="text"
              required
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              placeholder="Ex: Battle des Champions 2025"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Décrivez le défi et ses enjeux..."
              rows={3}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition resize-none"
            />
          </div>

          {/* Club à défier */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center space-x-2">
              <Users size={16} />
              <span>Club à défier *</span>
            </label>
            
            {clubs.length === 0 ? (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center">
                <p className="text-gray-400 text-sm">Aucun autre club disponible</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                {clubs.map(club => (
                  <div
                    key={club._id}
                    onClick={() => setFormData({ ...formData, clubDefieId: club._id })}
                    className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.clubDefieId === club._id
                        ? 'border-indigo-500 bg-indigo-500/10 shadow-lg shadow-indigo-500/20'
                        : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                    }`}
                  >
                    {/* Image du club */}
                    <img
                      src={club.imageClub || defaultGroupPic}
                      alt={club.nom}
                      className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover border-2 ${
                        formData.clubDefieId === club._id
                          ? 'border-indigo-500'
                          : 'border-slate-600'
                      }`}
                    />
                    
                    {/* Infos du club */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold truncate text-sm sm:text-base mb-0.5 ${
                        formData.clubDefieId === club._id ? 'text-white' : 'text-gray-300'
                      }`}>
                        {club.nom}
                      </h3>
                      <div className="flex items-center gap-2 sm:gap-3 text-xs text-gray-400">
                        <div className="flex items-center gap-1">
                          <MapPin size={12} />
                          <span className="truncate max-w-[120px] sm:max-w-none">{club.ville}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users size={12} />
                          <span>{club.nombreMembres}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Indicateur de sélection */}
                    <div className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${
                      formData.clubDefieId === club._id
                        ? 'border-indigo-500 bg-indigo-500'
                        : 'border-slate-600'
                    }`}>
                      {formData.clubDefieId === club._id && (
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Type de défi */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center space-x-2">
              <Target size={16} />
              <span>Type de défi *</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {typeDefiOptions.map(option => {
                const Icon = option.icon;
                const isSelected = formData.typeDefi === option.value;
                
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, typeDefi: option.value })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-500/10 shadow-lg shadow-indigo-500/20'
                        : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                    }`}
                  >
                    <Icon 
                      size={24} 
                      className={`mb-2 ${isSelected ? 'text-indigo-400' : 'text-gray-400'}`} 
                    />
                    <div className={`text-sm font-semibold mb-1 ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                      {option.label}
                    </div>
                    <div className="text-xs text-gray-500">
                      {option.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dates */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3 flex items-center space-x-2">
              <Calendar size={16} />
              <span>Période du défi *</span>
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-2">Date de début</label>
                <input
                  type="date"
                  required
                  value={formData.dateDebut}
                  onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-2">Date de fin</label>
                <input
                  type="date"
                  required
                  value={formData.dateFin}
                  onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
                  min={formData.dateDebut}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                />
              </div>
            </div>
          </div>

          {/* Points */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center space-x-2 mb-4">
              <Trophy size={18} className="text-yellow-400" />
              <h3 className="font-bold text-white">Points en jeu</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2">
                  Points pour le vainqueur
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={formData.pointsVainqueur}
                  onChange={(e) => setFormData({ ...formData, pointsVainqueur: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2">
                  Points pour le perdant
                </label>
                <input
                  type="number"
                  required
                  value={formData.pointsPerdant}
                  onChange={(e) => setFormData({ ...formData, pointsPerdant: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Le vainqueur gagne des points pour le classement global
            </p>
          </div>

          {/* Règles optionnelles */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center space-x-2 mb-4">
              <Settings size={18} className="text-gray-400" />
              <h3 className="font-bold text-white">Règles (optionnel)</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2">
                  Distance minimum par activité (km)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.regles.distanceMin}
                  onChange={(e) => setFormData({
                    ...formData,
                    regles: { ...formData.regles, distanceMin: parseFloat(e.target.value) || 0 }
                  })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                  placeholder="Ex: 5.0"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2">
                    Allure minimum (min/km)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.regles.allureMin || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      regles: { ...formData.regles, allureMin: parseFloat(e.target.value) || null }
                    })}
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                    placeholder="Ex: 7.0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2">
                    Allure maximum (min/km)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.regles.allureMax || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      regles: { ...formData.regles, allureMax: parseFloat(e.target.value) || null }
                    })}
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                    placeholder="Ex: 4.0"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer Actions */}
        <div className="border-t border-slate-700 p-6 bg-slate-900">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-xl border-2 border-slate-700 text-gray-300 font-semibold hover:bg-slate-800 transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold hover:shadow-lg hover:shadow-red-500/20 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Création...</span>
                </>
              ) : (
                <>
                  <Swords size={20} />
                  <span>Lancer le défi</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateClubWar;